import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { AppIcon } from '../../ui/AppIcon';
import { ImageCropDialog } from '../../ui/ImageCropDialog';
import { isValidBaseUrl, PROVIDER_PURPOSE_LABEL } from '../providers/draft';
import { EMPTY_VENDOR_LOCKS, PROTOCOL_LABELS } from '../providers/vendors';
import type { ProviderProtocol, ProviderVendor, VendorField } from '../providers/vendors';
import { resizeVendorLogo } from '../providers/vendorLogo';
import { AdminApiError, adminErrorMessage, adminFetch } from './api';
import { ComponentRegistry } from './ComponentRegistry';
import { DataTable } from './DataTable';
import type { Column } from './DataTable';
import styles from './admin.module.css';
import vendorStyles from './ProviderVendorsPanel.module.css';

const FIELD_LABELS: Record<VendorField, string> = { name: '厂商名', protocol: '协议', baseUrl: 'API 主机地址', purpose: '模型类型', logo: '厂商 Logo' };
type VendorDraft = Omit<ProviderVendor, 'id'> & { id?: string };
interface Props {
  query?: string;
  revision?: number;
  onAccessFailure: (failure: unknown) => void;
  onBusyChange?: (busy: boolean) => void;
}

function emptyDraft(): VendorDraft {
  return { name: '', protocol: 'chat-completions', baseUrl: '', purpose: 'chat', logo: '', locks: { ...EMPTY_VENDOR_LOCKS } };
}

function VendorLogo({ vendor }: { vendor: Pick<ProviderVendor, 'name' | 'logo'> }): ReactElement {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [vendor.logo]);
  return <span className={vendorStyles.logo}>{vendor.logo && !failed
    ? <img src={vendor.logo} alt={`${vendor.name || '厂商'} Logo`} referrerPolicy="no-referrer" onError={() => setFailed(true)} />
    : <span aria-hidden="true">{vendor.name.trim().slice(0, 1) || <AppIcon name="gallery" size={20} />}</span>}</span>;
}

export function ProviderVendorsPanel({ query = '', revision = 0, onAccessFailure, onBusyChange }: Props): ReactElement {
  const [vendors, setVendors] = useState<ProviderVendor[]>([]);
  const [draft, setDraft] = useState<VendorDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retry, setRetry] = useState(0);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [processingLogo, setProcessingLogo] = useState(false);
  const mounted = useRef(false);
  const mutation = useRef<AbortController | null>(null);
  const upload = useRef<HTMLInputElement | null>(null);
  const busy = loading || saving || processingLogo;

  const fail = useCallback((failure: unknown) => {
    setError(adminErrorMessage(failure));
    if (failure instanceof AdminApiError && [401, 403].includes(failure.status)) onAccessFailure(failure);
  }, [onAccessFailure]);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; mutation.current?.abort(); };
  }, []);

  useEffect(() => {
    const abort = new AbortController();
    setLoading(true); setLoadFailed(false); setVendors([]); setError('');
    void adminFetch<{ vendors: ProviderVendor[] }>('/admin/provider-vendors', { signal: abort.signal })
      .then((result) => { if (!abort.signal.aborted) setVendors(result.vendors); })
      .catch((failure: unknown) => { if (!abort.signal.aborted) { setLoadFailed(true); fail(failure); } })
      .finally(() => { if (!abort.signal.aborted) setLoading(false); });
    return () => abort.abort();
  }, [revision, retry, fail]);

  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);

  function edit(vendor?: ProviderVendor): void {
    setDraft(vendor ? { ...vendor, locks: { ...vendor.locks } } : emptyDraft());
    setError(''); setNotice('');
  }

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!draft || mutation.current || busy) return;
    if (!draft.name.trim()) { setError('请填写厂商名。'); return; }
    if (!isValidBaseUrl(draft.baseUrl)) { setError('请填写合法的 http(s) API 主机地址。'); return; }
    const baseUrl = new URL(draft.baseUrl.trim());
    if (baseUrl.username || baseUrl.password || baseUrl.search || baseUrl.hash) { setError('API 主机地址不能包含账号、密码、查询参数或片段。'); return; }
    if (draft.logo && !/^https:\/\//i.test(draft.logo.trim()) && !/^data:image\/(png|jpeg|webp|gif);base64,/.test(draft.logo.trim())) {
      setError('请上传图片或填写 HTTPS Logo 地址。'); return;
    }
    const abort = new AbortController(); mutation.current = abort;
    setSaving(true); setError(''); setNotice('');
    try {
      const { id } = draft;
      const body = { name: draft.name.trim(), protocol: draft.protocol, baseUrl: draft.baseUrl.trim(), purpose: draft.purpose, logo: draft.logo.trim(), locks: { ...draft.locks } };
      const { vendor } = await adminFetch<{ vendor: ProviderVendor }>(id ? `/admin/provider-vendors/${encodeURIComponent(id)}` : '/admin/provider-vendors', { method: id ? 'PATCH' : 'POST', body, signal: abort.signal });
      if (abort.signal.aborted || !mounted.current) return;
      setVendors((current) => id ? current.map((entry) => entry.id === id ? vendor : entry) : [...current, vendor]);
      setDraft(null); setNotice(id ? '模型厂商已保存。' : '模型厂商已添加。');
    } catch (failure) { if (!abort.signal.aborted && mounted.current) fail(failure); }
    finally { if (mutation.current === abort) mutation.current = null; if (!abort.signal.aborted && mounted.current) setSaving(false); }
  }

  async function remove(vendor: ProviderVendor): Promise<void> {
    if (busy || mutation.current || !window.confirm(`确认删除模型厂商「${vendor.name}」？已添加的用户提供商配置会保留。`)) return;
    const abort = new AbortController(); mutation.current = abort;
    setSaving(true); setError(''); setNotice('');
    try {
      await adminFetch(`/admin/provider-vendors/${encodeURIComponent(vendor.id)}`, { method: 'DELETE', signal: abort.signal });
      if (!abort.signal.aborted && mounted.current) { setVendors((current) => current.filter((entry) => entry.id !== vendor.id)); setNotice('模型厂商已删除。'); }
    } catch (failure) { if (!abort.signal.aborted && mounted.current) fail(failure); }
    finally { if (mutation.current === abort) mutation.current = null; if (!abort.signal.aborted && mounted.current) setSaving(false); }
  }

  function field(key: VendorField, control: ReactNode): ReactElement {
    return <div className={vendorStyles.field} key={key}>
      <div className={vendorStyles.fieldHeading}><label htmlFor={`vendor-${key}`}>{FIELD_LABELS[key]}</label>
        <label className={vendorStyles.lock}><input type="checkbox" aria-label={`锁定${FIELD_LABELS[key]}`} checked={draft?.locks[key] ?? false} disabled={busy} onChange={(event) => setDraft((current) => current ? { ...current, locks: { ...current.locks, [key]: event.target.checked } } : null)} />锁定</label>
      </div>{control}
    </div>;
  }

  async function confirmLogo(source: string): Promise<void> {
    setCropFile(null); setProcessingLogo(true); setError('');
    try {
      const logo = await resizeVendorLogo(source);
      if (mounted.current) setDraft((current) => current ? { ...current, logo } : null);
    } catch { if (mounted.current) setError('无法处理厂商 Logo，请重新选择图片。'); }
    finally { if (mounted.current) setProcessingLogo(false); }
  }

  const columns: Column<ProviderVendor>[] = [
    { label: '厂商', render: (vendor) => <div className={vendorStyles.identity}><VendorLogo vendor={vendor} /><strong>{vendor.name}</strong></div> },
    { label: '协议', render: (vendor) => PROTOCOL_LABELS[vendor.protocol] },
    { label: 'API 主机地址', render: (vendor) => <span className={vendorStyles.address}>{vendor.baseUrl}</span> },
    { label: '模型类型', render: (vendor) => PROVIDER_PURPOSE_LABEL[vendor.purpose] },
    { label: '锁定字段', render: (vendor) => (Object.keys(FIELD_LABELS) as VendorField[]).filter((key) => vendor.locks[key]).map((key) => FIELD_LABELS[key]).join('、') || '无' },
    { label: '操作', render: (vendor) => <div className={styles.actions}>
      <button type="button" title={`编辑 ${vendor.name}`} aria-label={`编辑 ${vendor.name}`} disabled={busy} onClick={() => edit(vendor)}><AppIcon name="edit" size={18} /></button>
      <button type="button" title={`删除 ${vendor.name}`} aria-label={`删除 ${vendor.name}`} className={styles.danger} disabled={busy} onClick={() => void remove(vendor)}><AppIcon name="delete" size={18} /></button>
    </div> },
  ];

  return <div className={vendorStyles.panel} aria-busy={busy}>
    {draft ? <ComponentRegistry id="admin.vendors.editor"><form onSubmit={(event) => void save(event)}>
      <div className={styles.toolbar}><h3>{draft.id ? '编辑模型厂商' : '添加模型厂商'}</h3><div className={styles.actions}>
        <button type="button" disabled={busy} onClick={() => { setDraft(null); setCropFile(null); setError(''); }}><AppIcon name="back" size={18} />返回列表</button>
        <button type="submit" className={styles.primary} disabled={busy || cropFile !== null}><AppIcon name="check" size={18} />{saving ? '保存中…' : '保存厂商'}</button>
      </div></div>
      <div className={vendorStyles.fields}>
        {field('name', <input id="vendor-name" required maxLength={80} disabled={busy} value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />)}
        {field('protocol', <select id="vendor-protocol" disabled={busy} value={draft.protocol} onChange={(event) => { const protocol = event.target.value as ProviderProtocol; setDraft({ ...draft, protocol, purpose: protocol === 'chat-completions' ? draft.purpose : 'chat' }); }}>
          {(Object.keys(PROTOCOL_LABELS) as ProviderProtocol[]).map((protocol) => <option key={protocol} value={protocol}>{PROTOCOL_LABELS[protocol]}</option>)}
        </select>)}
        {field('baseUrl', <input id="vendor-baseUrl" type="url" required maxLength={2048} placeholder="https://api.example.com/v1" disabled={busy} value={draft.baseUrl} onChange={(event) => setDraft({ ...draft, baseUrl: event.target.value })} />)}
        {field('purpose', <select id="vendor-purpose" disabled={busy} value={draft.purpose} onChange={(event) => setDraft({ ...draft, purpose: event.target.value as ProviderVendor['purpose'] })}>
          <option value="chat">{PROVIDER_PURPOSE_LABEL.chat}</option>{draft.protocol === 'chat-completions' && <><option value="image">{PROVIDER_PURPOSE_LABEL.image}</option><option value="tts">{PROVIDER_PURPOSE_LABEL.tts}</option></>}
        </select>)}
        {field('logo', <div className={vendorStyles.logoEditor}><VendorLogo vendor={draft} /><div className={vendorStyles.logoControls}>
          <input id="vendor-logo" type="text" aria-label="厂商 Logo 地址" placeholder="https://example.com/logo.png" disabled={busy} value={draft.logo.startsWith('data:') ? '' : draft.logo} onChange={(event) => setDraft({ ...draft, logo: event.target.value })} />
          <div className={styles.actions}><button type="button" disabled={busy} onClick={() => upload.current?.click()}><AppIcon name="upload" size={18} />上传 Logo</button>
            <button type="button" title="清除 Logo" aria-label="清除 Logo" disabled={busy || !draft.logo} onClick={() => setDraft({ ...draft, logo: '' })}><AppIcon name="delete" size={18} /></button>
          </div><input ref={upload} hidden type="file" accept="image/png,image/jpeg,image/webp,image/gif" aria-label="上传厂商 Logo" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; if (file) setCropFile(file); }} />
        </div></div>)}
      </div>
    </form></ComponentRegistry> : <>
      <div className={styles.toolbar}><button type="button" className={styles.primary} disabled={busy} onClick={() => edit()}><AppIcon name="new-chat" size={18} />添加模型厂商</button></div>
      {loading ? <p role="status" className={styles.empty}>正在加载模型厂商…</p> : loadFailed ? <p className={styles.empty}>无法加载模型厂商。</p> : <DataTable label="模型厂商" rows={vendors.filter((vendor) => `${vendor.name} ${vendor.baseUrl} ${PROTOCOL_LABELS[vendor.protocol]} ${PROVIDER_PURPOSE_LABEL[vendor.purpose]}`.toLowerCase().includes(query.trim().toLowerCase()))} columns={columns} busy={busy} />}
    </>}
    <ComponentRegistry id="admin.vendors.status">{error && <div role="alert" className={styles.error}>{error}{!draft && <button type="button" disabled={busy} onClick={() => setRetry((value) => value + 1)}>重试</button>}</div>}{notice && !error && <p role="status" className={styles.success}>{notice}</p>}</ComponentRegistry>
    {cropFile && <ImageCropDialog file={cropFile} kind="avatar" onCancel={() => setCropFile(null)} onConfirm={(logo) => void confirmLogo(logo)} />}
  </div>;
}
